import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';
import BrandLogo from '@/components/BrandLogo';

export default function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const { login, loginWithGoogle } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const timeoutPromise = new Promise((_, reject) => setTimeout(() => reject(new Error('Login timed out. Please try again.')), 15000));
      const user = await Promise.race([login(email, password), timeoutPromise]);
      if (!user || !user.role) throw new Error('Login succeeded but profile not loaded. Please refresh and try again.');
      if (user.force_password_change) {
        navigate('/change-password');
        return;
      }
      toast.success('Welcome back!');
      navigate(user.role === 'admin' ? '/admin' : user.role === 'driver' ? '/driver' : '/client');
    } catch (err) {
      toast.error(err.message || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-6" data-testid="login-page">
      <div className="w-full max-w-md">
        <Button variant="ghost" className="text-muted-foreground hover:text-foreground mb-8 -ml-2" onClick={() => navigate('/')} data-testid="back-btn">
          <ArrowLeft className="w-4 h-4 mr-2" /> Back
        </Button>

        <Card className="bg-card border-border rounded-2xl">
          <CardHeader className="text-center pb-2">
            <div className="flex justify-center mb-4">
              <BrandLogo size="lg" />
            </div>
            <CardTitle className="text-foreground text-lg font-medium">Sign in to your account</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <Button variant="outline" className="w-full border-border text-muted-foreground hover:bg-muted rounded-xl h-12"
              onClick={loginWithGoogle} data-testid="google-login-btn">
              <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24"><path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/><path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/><path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/><path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/></svg>
              Continue with Google
            </Button>

            <div className="flex items-center gap-3">
              <div className="flex-1 h-px bg-border" />
              <span className="text-muted-foreground text-xs">or</span>
              <div className="flex-1 h-px bg-border" />
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label className="text-muted-foreground text-xs">Email</Label>
                <Input type="email" value={email} onChange={e => setEmail(e.target.value)} required
                  className="bg-muted border-input text-foreground rounded-xl h-11 mt-1 focus:border-brand-accent"
                  placeholder="admin@freightos.com" data-testid="login-email-input" />
              </div>
              <div>
                <Label className="text-muted-foreground text-xs">Password</Label>
                <Input type="password" value={password} onChange={e => setPassword(e.target.value)} required
                  className="bg-muted border-input text-foreground rounded-xl h-11 mt-1 focus:border-brand-accent"
                  placeholder="Enter password" data-testid="login-password-input" />
              </div>
              <Button type="submit" disabled={loading}
                className="w-full bg-brand-accent hover:bg-brand-accent/90 text-brand-accent-foreground font-semibold rounded-full h-12"
                data-testid="login-submit-btn">
                {loading ? 'Signing in...' : 'Sign In'}
              </Button>
            </form>

            <div className="text-center pt-2">
              <p className="text-muted-foreground text-sm">No account? <Link to="/register" className="text-brand-accent hover:underline" data-testid="register-link">Create one</Link></p>
            </div>

            <div className="bg-muted/50 border border-border rounded-xl p-3 mt-4">
              <p className="text-muted-foreground text-xs">Demo Accounts:</p>
              <p className="text-muted-foreground/70 text-xs mt-1">Admin: admin@freightos.com / password</p>
              <p className="text-muted-foreground/70 text-xs">Driver: driver@freightos.com / password</p>
              <p className="text-muted-foreground/70 text-xs">Client: client@freightos.com / password</p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
